import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import PropertyCard from '@/components/PropertyCard';
import { getFeaturedProperties, toggleSaveProperty, getSavedProperties } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { ArrowRight, Map, PlusCircle, ShieldCheck, Smartphone, ChevronDown } from 'lucide-react';
import Seo from '@/components/Seo';
import AISmartSearch from '@/components/AISmartSearch';
import { TrustStrip, TrendingCities, Testimonials } from '@/components/HomeSections';
import { NewLaunchProjects } from '@/components/NewLaunchProjects';
import { LocalPropertySections } from '@/components/LocalPropertySections';
import { HeroSearch } from '@/components/HeroSearch';

const TYPES = [
  { type: 'apartment', label: 'Apartments', image: 'https://images.unsplash.com/photo-1738168279272-c08d6dd22002?crop=entropy&cs=srgb&fm=jpg&w=600&q=80' },
  { type: 'villa', label: 'Villas', image: 'https://images.unsplash.com/photo-1629946488804-217c002178cf?crop=entropy&cs=srgb&fm=jpg&w=600&q=80' },
  { type: 'house', label: 'Independent Houses', image: 'https://images.unsplash.com/photo-1764011154918-977e82781fdc?crop=entropy&cs=srgb&fm=jpg&w=600&q=80' },
  { type: 'plot', label: 'Plots & Land', image: 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?crop=entropy&cs=srgb&fm=jpg&w=600&q=80' },
  { type: 'commercial', label: 'Commercial', image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?crop=entropy&cs=srgb&fm=jpg&w=600&q=80' },
];

const TOOLS = [
  { href: '/emi-calculator', label: 'EMI Calculator', desc: 'Plan your loan' },
  { href: '/affordability-calculator', label: 'Affordability', desc: 'Buying power' },
  { href: '/stamp-duty-calculator', label: 'Stamp Duty', desc: 'State-wise' },
  { href: '/rental-yield-calculator', label: 'Rental Yield', desc: 'ROI estimator' },
  { href: '/market-trends', label: 'Market Insights', desc: '8 cities' },
  { href: '/quiz', label: 'Property Match Quiz', desc: '60 seconds' },
  { href: '/glossary', label: 'Glossary', desc: '30+ terms' },
  { href: '/compare', label: 'Compare', desc: 'Side-by-side' },
];

const FAQS = [
  ['Is Bharat Estate free for buyers?', 'Yes. Browsing, saving, enquiring and contacting sellers is free. There is 0% brokerage on direct-owner listings.'],
  ['How do I post a property?', 'Click Post Property, fill in the details, drop the exact map pin and add photos. Your first month of Premium is free; after that a paid plan is required to keep listings live.'],
  ['Are the listings verified?', 'Developer projects are sourced from official developer channels and carry RERA details where available. Owner listings are reviewed by our team before going live.'],
  ['Can I search by map?', 'Yes — use Map Search to pan around any neighbourhood and see live price pins for every listing in view.'],
];

const SectionHead = ({ eyebrow, title, link, linkLabel = 'View all' }) => (
  <div className="flex items-end justify-between gap-6 mb-8">
    <div>
      {eyebrow && <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">{eyebrow}</p>}
      <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0F172A]">{title}</h2>
    </div>
    {link && <Link to={link} className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-[#0F172A] hover:text-[#B5952F]" data-testid="view-all-properties">{linkLabel} <ArrowRight className="h-4 w-4" /></Link>}
  </div>
);

const HomePage = () => {
  const [featured, setFeatured] = useState([]);
  const [savedIds, setSavedIds] = useState(new Set());
  const [openFaq, setOpenFaq] = useState(0);
  const { user } = useAuth();

  useEffect(() => {
    getFeaturedProperties().then(setFeatured).catch(() => {});
    if (user) getSavedProperties().then((s) => setSavedIds(new Set(s.map((p) => p.property_id)))).catch(() => {});
  }, [user]);

  const handleSave = async (propertyId) => {
    if (!user) return toast.error('Please login to save properties');
    try {
      const result = await toggleSaveProperty(propertyId);
      setSavedIds((prev) => { const n = new Set(prev); result.saved ? n.add(propertyId) : n.delete(propertyId); return n; });
      toast.success(result.saved ? 'Property saved' : 'Removed from saved');
    } catch { toast.error('Failed to save property'); }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7]">
      <Seo title="Buy, Sell & Rent Property Across India" description="Find a property that fits your life. Verified apartments, villas, plots and commercial spaces across 24+ Indian cities with map search and 0% brokerage." path="/" />
      <HeroSearch />
      <TrustStrip />
      <LocalPropertySections />

      <section className="py-16 md:py-24">
        <div className="container-luxury">
          <SectionHead eyebrow="Curated" title="Featured Properties" link="/properties" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.slice(0, 6).map((p) => <PropertyCard key={p.property_id} property={p} onSave={handleSave} isSaved={savedIds.has(p.property_id)} />)}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-white border-y border-slate-200">
        <div className="container-luxury">
          <SectionHead eyebrow="Browse by type" title="What are you looking for?" />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {TYPES.map((t) => (
              <Link key={t.type} to={`/properties?property_type=${t.type}`} className="group relative aspect-[4/5] overflow-hidden rounded-2xl" data-testid={`property-type-${t.type}`}>
                <img src={t.image} alt={t.label} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent" />
                <div className="absolute bottom-0 p-4"><h3 className="text-white font-bold">{t.label}</h3><p className="text-white/70 text-xs mt-0.5 group-hover:text-[#D4AF37]">Explore →</p></div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container-luxury grid lg:grid-cols-2 gap-6">
          <Link to="/map-search" className="group relative rounded-3xl overflow-hidden min-h-[320px] flex items-end bg-[#0F172A]" data-testid="home-map-cta">
            <img src="https://images.unsplash.com/photo-1524661135-423995f22d0b?auto=format&fit=crop&w=1400&q=80" alt="" className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] via-[#0F172A]/40 to-transparent" />
            <div className="relative p-8 md:p-10">
              <span className="inline-flex items-center gap-1.5 text-xs font-bold text-[#D4AF37] uppercase tracking-wider mb-3"><Map className="h-4 w-4" /> Map Search</span>
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">Explore every neighbourhood on the map</h3>
              <p className="text-white/70 mb-5 max-w-md">Live price pins, draw your own search area and compare localities side by side.</p>
              <span className="inline-flex items-center gap-2 bg-white text-[#0F172A] font-semibold rounded-full px-5 py-2.5 group-hover:gap-3 transition-all">Open the map <ArrowRight className="h-4 w-4" /></span>
            </div>
          </Link>
          <div className="rounded-3xl bg-white border border-slate-200 p-8 md:p-10 flex flex-col justify-between">
            <div>
              <span className="inline-flex items-center gap-1.5 text-xs font-bold text-[#047857] uppercase tracking-wider mb-3"><PlusCircle className="h-4 w-4" /> Owners & agents</span>
              <h3 className="text-2xl md:text-3xl font-bold text-[#0F172A] mb-2">List your property in minutes</h3>
              <p className="text-slate-600 mb-6 max-w-md">Reach serious buyers and tenants with a map-pinned, photo-rich listing. First month of Premium is on us — then simple monthly plans from ₹99.</p>
              <ul className="space-y-2 text-sm text-slate-700">
                {['Exact map pin + directions for visitors', 'Enquiries, calls and WhatsApp in one inbox', 'RERA-aware, verified badge on review'].map((t) => <li key={t} className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#047857]" /> {t}</li>)}
              </ul>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/add-property"><Button className="h-12 rounded-full bg-[#0F172A] hover:bg-[#1E293B] text-white px-6 font-semibold" data-testid="home-post-property-btn">Post Property — Free</Button></Link>
              <Link to="/premium"><Button variant="outline" className="h-12 rounded-full px-6 font-semibold border-slate-300">See plans</Button></Link>
            </div>
          </div>
        </div>
      </section>

      <NewLaunchProjects />
      <TrendingCities />
      <AISmartSearch />

      <section className="py-16 md:py-24 bg-white border-y border-slate-200" data-testid="home-tools-section">
        <div className="container-luxury">
          <SectionHead eyebrow="Plan smarter" title="Tools for confident decisions" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {TOOLS.map((t) => (
              <Link key={t.href} to={t.href} className="rounded-2xl border border-slate-200 p-5 hover:border-[#0F172A] hover:-translate-y-0.5 transition-all group" data-testid={`home-tool-${t.href.replace('/', '')}`}>
                <h4 className="font-bold text-[#0F172A] text-sm">{t.label}</h4>
                <p className="text-xs text-slate-500 mt-1">{t.desc}</p>
                <ArrowRight className="h-4 w-4 mt-4 text-slate-300 group-hover:text-[#0F172A] group-hover:translate-x-1 transition-all" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Testimonials />

      <section className="py-16 md:py-24">
        <div className="container-luxury grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">FAQ</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0F172A] mb-4">Questions, answered</h2>
            <p className="text-slate-600 mb-6">Still unsure? Our team is on WhatsApp and phone every day.</p>
            <div className="rounded-2xl bg-[#0F172A] text-white p-6 flex items-center gap-4">
              <Smartphone className="h-8 w-8 text-[#D4AF37] flex-shrink-0" />
              <div><p className="font-bold">Bharat Estate app — coming soon</p><p className="text-sm text-white/70">Same listings, same account, instant alerts on your phone.</p></div>
            </div>
          </div>
          <div className="lg:col-span-3 divide-y divide-slate-200 rounded-2xl bg-white border border-slate-200" data-testid="home-faq">
            {FAQS.map(([q, a], i) => (
              <button key={q} onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="w-full text-left p-5 flex gap-4 items-start" data-testid={`faq-${i}`}>
                <ChevronDown className={`h-5 w-5 mt-0.5 text-slate-400 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                <div><p className="font-semibold text-[#0F172A]">{q}</p>{openFaq === i && <p className="text-sm text-slate-600 mt-2 leading-relaxed">{a}</p>}</div>
              </button>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
